import React from 'react';
import { Avatar, Box, Collapse, Paper, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { ProviderOverview, UpliftLicenseType, User } from '@uplift/api';
import { ProviderCardContacts } from './ProviderCardContacts.component';
import { useToggleObject } from '../../../../../hooks';
import { getProviderLabelByMedicalRoleAndPrescriberTypes } from '../../../../../molecules/Lists/CareTeamList/helpers';


const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  summary: {
    padding: theme.spacing('10px', 1),
    display: 'flex',
    alignItems: 'center',
  },
  summaryExpandable: {
    cursor: 'pointer',
  },
  summaryContent: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
  },
  summaryIcon: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    paddingRight: theme.spacing(1),
  },
  avatar: {
    width: '40px',
    height: '40px'
  },
  details: {
    marginLeft: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  userName: {
    lineHeight: 1,
  }
}));


export type ProviderCardProps = {
  expandable?: boolean;
  user: Pick<User, 'id' | 'firstName' | 'lastName' | 'medicalRole'>;
  overview: Pick<ProviderOverview, 'displayName'>;
  providerLicensesTypes: UpliftLicenseType[];
  avatarUrl?: string;
};


export const ProviderCard: React.VFC<ProviderCardProps> = ({ expandable, providerLicensesTypes, user, overview, avatarUrl }) => {
  const { classes, cx } = useStyles();
  const { value: open, toggle: handleToggle } = useToggleObject(false);

  const medicalTitle = React.useMemo(() => {
    if (!user.medicalRole) return '–';

    return getProviderLabelByMedicalRoleAndPrescriberTypes(
      user.medicalRole,
      providerLicensesTypes,
    );
  }, [ user.medicalRole, providerLicensesTypes ]);

  return (
    <Paper className={classes.root} component="div" variant="outlined">
      <Box className={cx(classes.summary, { [classes.summaryExpandable]: expandable })} onClick={handleToggle}>
        <Box className={classes.summaryContent}>
          <Avatar className={classes.avatar} variant="square" src={avatarUrl} />
          <Box className={classes.details}>
            <Typography className={classes.userName}>{overview.displayName ?? `${user.firstName} ${user.lastName}`}</Typography>
            <Typography variant="body2">{medicalTitle}</Typography>
          </Box>
        </Box>
        {expandable && (
          <Box className={classes.summaryIcon}>
            {open ? <ExpandLess color="action" /> : <ExpandMore color="action" />}
          </Box>
        )}
      </Box>
      {expandable && (
        <Collapse in={open}>
          <ProviderCardContacts providerId={user.id} medicalRole={user.medicalRole ?? undefined} visible={open} />
        </Collapse>
      )}
    </Paper>
  );
};
